import { type FC } from "react";

import dompurify from "isomorphic-dompurify";

import { AlertBanner } from "@jti/core-components/components/AlertBanner";
import { Button } from "@jti/core-components/components/Button";
import { Form, useForm, useTranslations } from "@jti/ui";

import { OrderAttributesSlot, PaymentSecure } from "@checkout-v2/features";
import { usePaymentProviderContext } from "@checkout-v2/features/payment";
import { CheckboxField } from "@checkout-v2/formFields";
import type { SubmitHandler } from "@checkout-v2/forms";
import { PAYMENT_ADAPTER_MOUNT_NODE_ID } from "@checkout-v2/shared/const";

import type {
  ConsentAndPayFormFields,
  ConsentAndPayFormFieldsConfig,
  ConsentAndPayFormFieldsDefaultValues,
} from "../ConsentAndPayForm.types";

import styles from "./ConsentAndPayFormRenderer.module.scss";

export type ConsentAndPayFormRendererProps = {
  formFieldsConfig: ConsentAndPayFormFieldsConfig;
  defaultValues: ConsentAndPayFormFieldsDefaultValues;
  onSubmit: SubmitHandler<ConsentAndPayFormFields>;
  isPayOnDelivery?: boolean;
  isFreeOfCharge?: boolean;
  isConsentEnabled: boolean;
  isRetryingPayment?: boolean;
  isSubmitButtonDisabled?: boolean;
  isPaymentMethodPreselected?: boolean;
  isOrderPagePaymentFlowEnabled?: boolean;
  hasPaymentAdapter?: boolean;
  orderWarningMessage?: string;
};

const getButtonTextTranslationKey = (
  isPayOnDelivery: boolean,
  isFreeOfCharge: boolean,
  isRetryingPayment: boolean,
  isOrderPagePaymentFlowEnabled = false
) => {
  if (isRetryingPayment) {
    return "cart.order.payment.retry";
  }

  if (isPayOnDelivery || isFreeOfCharge) {
    return "cart.order.payment.finalize";
  }

  if (isOrderPagePaymentFlowEnabled) {
    return "cart.order.payment.confirm_pay";
  }

  return "cart.order.payment.pay";
};

const { sanitize } = dompurify;

export const ConsentAndPayFormRenderer: FC<ConsentAndPayFormRendererProps> = ({
  formFieldsConfig,
  defaultValues,
  onSubmit,
  isPayOnDelivery = false,
  isFreeOfCharge = false,
  isConsentEnabled,
  isSubmitButtonDisabled = false,
  isRetryingPayment = false,
  isPaymentMethodPreselected = false,
  isOrderPagePaymentFlowEnabled = false,
  hasPaymentAdapter = false,
  orderWarningMessage,
}) => {
  const { __ } = useTranslations();
  const form = useForm<ConsentAndPayFormFields>({ defaultValues, mode: "onChange" });

  const buttonText = __(
    getButtonTextTranslationKey(isPayOnDelivery, isFreeOfCharge, !!isRetryingPayment, isOrderPagePaymentFlowEnabled)
  );
  const { paymentInitiationStatus, adapterStatus, secureFieldsPaymentInitiationStatus } = usePaymentProviderContext();

  const consentValue = form.watch("consent");
  const isConsentMissing = isConsentEnabled && !consentValue;

  const shouldShowOrderWarning = isOrderPagePaymentFlowEnabled && !isPaymentMethodPreselected && !!orderWarningMessage;
  const shouldShowPaymentSecure = !isOrderPagePaymentFlowEnabled || isPaymentMethodPreselected;

  const handleSubmit = async (data: ConsentAndPayFormFields) => {
    await onSubmit(data, form);
  };

  if (hasPaymentAdapter) {
    return (
      <div className={styles.container}>
        <div id={PAYMENT_ADAPTER_MOUNT_NODE_ID} />
        {shouldShowPaymentSecure && <PaymentSecure />}
      </div>
    );
  }

  return (
    <Form form={form} onSubmit={handleSubmit}>
      <div className={styles.container}>
        <div className={styles.field}>
          <OrderAttributesSlot slotTarget="buttonPlaceOrder_before" />
        </div>

        {isConsentEnabled && (
          <div className={styles.field}>
            <CheckboxField fieldConfig={formFieldsConfig.consent} />
          </div>
        )}

        {shouldShowOrderWarning && (
          <div className={styles.warning}>
            <AlertBanner
              tone="info"
              richDescription={<div dangerouslySetInnerHTML={{ __html: sanitize(orderWarningMessage) }}></div>}
            />
          </div>
        )}

        <div id={PAYMENT_ADAPTER_MOUNT_NODE_ID}>
          <Button
            type="submit"
            disabled={isSubmitButtonDisabled || isConsentMissing}
            pending={
              form.formState.isSubmitting ||
              paymentInitiationStatus === "loading" ||
              adapterStatus === "loading" ||
              secureFieldsPaymentInitiationStatus === "loading"
            }
          >
            {buttonText}
          </Button>
        </div>
        {shouldShowPaymentSecure && <PaymentSecure />}
      </div>
    </Form>
  );
};
